import React, { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { CheckCircle2, XCircle, Undo2, Hourglass } from 'lucide-react';

export const APPROVER_ROLES = ['Admin', 'ProductionSupervisor'];

const BADGE = {
  pending:   ['bg-amber-50 text-amber-800 border-amber-200', 'Pending approval'],
  rejected:  ['bg-rose-50 text-rose-700 border-rose-200', 'Changes requested'],
  approved:  ['bg-emerald-50 text-emerald-700 border-emerald-200', 'Approved'],
  withdrawn: ['bg-slate-50 text-slate-600 border-slate-200', 'Withdrawn'],
};

export const ApprovalBadge = ({ draft }) => {
  const a = draft.approval;
  if (!a || (a.status === 'approved' && draft.status === 'Promoted') || a.status === 'withdrawn') return null;
  const [cls, label] = BADGE[a.status] || BADGE.pending;
  return (
    <Badge className={`border text-[10px] ${cls}`} title={a.comment || ''} data-testid={`rd-approval-badge-${draft.id}`}>
      {a.status === 'pending' && <Hourglass className="w-3 h-3 mr-0.5" />}{label}{a.status === 'rejected' && a.by_name ? ` by ${a.by_name}` : ''}
    </Badge>
  );
};

export const ApprovalActions = ({ draft, onChange }) => {
  const { api, user } = useAuth();
  const [rejectOpen, setRejectOpen] = useState(false);
  const [comment, setComment] = useState('');
  const [busy, setBusy] = useState(false);
  const pending = draft.approval?.status === 'pending';
  const isApprover = APPROVER_ROLES.includes(user?.role);
  if (!pending) return null;
  const base = `/rd/projects/${draft.project_id}/draft-boms/${draft.id}`;

  const call = async (path, body, ok) => {
    setBusy(true);
    try { const r = await api().post(`${base}/${path}`, body); toast.success(ok(r.data)); setRejectOpen(false); onChange(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Action failed'); }
    finally { setBusy(false); }
  };

  return (
    <>
      {isApprover && <>
        <Button size="sm" disabled={busy} className="bg-emerald-600 hover:bg-emerald-700" onClick={() => call('approve', {}, d => `Approved — production rev ${d?.bom?.bom_revision}`)} data-testid={`rd-approve-btn-${draft.id}`}>
          <CheckCircle2 className="w-3.5 h-3.5 mr-1" />Approve
        </Button>
        <Button size="sm" variant="outline" disabled={busy} className="text-rose-700" onClick={() => { setComment(''); setRejectOpen(true); }} data-testid={`rd-reject-btn-${draft.id}`}>
          <XCircle className="w-3.5 h-3.5 mr-1" />Reject
        </Button>
      </>}
      {(draft.approval?.submitted_by_user_id === user?.id || user?.role === 'Admin') && (
        <Button size="sm" variant="ghost" disabled={busy} onClick={() => call('withdraw-approval', {}, () => 'Approval request withdrawn')} data-testid={`rd-withdraw-btn-${draft.id}`}>
          <Undo2 className="w-3.5 h-3.5 mr-1" />Withdraw
        </Button>
      )}
      <Dialog open={rejectOpen} onOpenChange={setRejectOpen}>
        <DialogContent className="max-w-md" data-testid="rd-reject-dialog">
          <DialogHeader><DialogTitle>Reject promotion of {draft.name}</DialogTitle></DialogHeader>
          <p className="text-xs text-slate-500">The draft goes back to R&D for changes. Your comment is sent to the submitter and kept in the History.</p>
          <Textarea rows={3} value={comment} onChange={e => setComment(e.target.value)} placeholder="What needs to change?" data-testid="rd-reject-comment" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectOpen(false)}>Cancel</Button>
            <Button disabled={busy || !comment.trim()} className="bg-rose-600 hover:bg-rose-700" onClick={() => call('reject', { comment }, () => 'Rejected. Sent back to R&D')} data-testid="rd-reject-submit-btn">Reject</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
